import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useSelector } from 'react-redux';
import {
    ArrowDownward as ArrowDownwardIcon,
    ArrowUpward as ArrowUpwardIcon,
    ListAlt as ListAltIcon,
    MoreHoriz as MoreHorizIcon,
    SwapHoriz as SwapHorizIcon,
    Store as StoreIcon,
} from '@mui/icons-material';

import { useJunoCommonFunctions } from '../../client/Juno/helpers';
import ActionButton from './ActionButton';
import MoreActionDropdown from './common/MoreActionDropdown';
import { cryptoTransactions, fiatTransactions, MODAL_COMPONENTS } from '../helpers/constant';
import useHandleActionClick from '../helpers/usehandleActionClick';
import { useParams } from 'react-router-dom';

const ActionBar = () => {
    const clientData = useSelector(state => state.juno.clientData);
    const kycStatus = localStorage.getItem('kycStatus');
    const readOnly = clientData && clientData.readOnly ? clientData.readOnly : false;
    const activeCurrencyPage = useSelector(state => state.juno.activeCurrencyPage);
    const [modalAction, setModalAction] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const moreButtonRef = useRef(null);
    const { getClientFeeSetting, useWindowWidth } = useJunoCommonFunctions();

    const windowWidth = useWindowWidth();

    const { balancesCurrencies, balancesCrypto } = useSelector(state => state.juno);

    const { currency } = useParams();
    const balancesData = [...balancesCurrencies, ...balancesCrypto];
    const currencyData = balancesData?.find(
        currencyData => currencyData.currencyShortName === currency,
    );

    const actionIcons = activeCurrencyPage === 'fiat' ? fiatTransactions : cryptoTransactions;

    const shouldShowMoreActions = activeCurrencyPage !== 'crypto' && windowWidth < 640;
    const handleActionClick = useHandleActionClick(kycStatus, setModalAction);

    const handleCloseMenu = useCallback(() => setMenuOpen(false), []);

    // More button -->
    const handleMoreClick = useCallback(e => {
        e.stopPropagation();
        setMenuOpen(prevOpen => !prevOpen);
    }, []);

    useEffect(() => {
        if (!menuOpen) return;

        const handleClickOutside = e => {
            if (moreButtonRef.current && !moreButtonRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen]);

    useEffect(() => {
        if (!modalAction || !clientData?.clientId) return;

        const payload = {
            transactionType: modalAction,
            currencyType: 'fiat',
        };

        getClientFeeSetting(payload);
    }, [modalAction]);

    // Handle modal closing
    const handleCloseModal = useCallback(() => setModalAction(''), []);

    const commonModalProps = {
        onClose: handleCloseModal,
        row: currencyData,
        selectCurrency: true,
        isModal: true,
        type: modalAction,
        open: true,
    };

    const ModalComponent = MODAL_COMPONENTS[modalAction];

    return (
        <div className="bg-none lg:bg-[#FFFFFF] dark:lg:bg-[#26272B] lg:border border-[#0000001F] dark:!border-[#FFFFFF1F] !p-1 rounded-xl relative flex flex-nowrap lg:grid lg:grid-cols-7 !md:gap-3 xl:gap-0.5 justify-center mt-1 lg:!mt-0 gap-[10px] !mb-3 lg:!mb-0">
            {[
                ...(windowWidth >= 640
                    ? actionIcons.slice(0, 7)
                    : activeCurrencyPage === 'crypto'
                      ? actionIcons.slice(0, 7)
                      : actionIcons.slice(0, 4)),
            ].map(({ key, label, icon, mobileLabel }) => (
                <div key={key} className="flex flex-col items-center justify-center lg:w-full">
                    <ActionButton
                        icon={icon}
                        onClick={() => handleActionClick(key)}
                        text={label.toUpperCase()}
                        disabled={readOnly}
                        htmlType="button"
                        type="contained"
                        dataE2e={`${mobileLabel || label}-btn`}
                        autoWidth={true}
                        shape="rounded-lg"
                        tooltipText=""
                        className="lg:w-full flex flex-col items-center justify-center  !bg-secondary !text-text p-3 md:p-6 xl:w-full hover:bg-[#D1D1D6] dark:hover:bg-[#51525C] transition flex-shrink-0 rounded-lg"
                        textClass="mt-1 text-[10px] md:text-xs text-center hidden lg:block font-medium uppercase"
                    />
                    <span className="mt-1 text-[10px] xl:text-xs text-center text-gray-custom xl:text-text lg:hidden font-semibold uppercase">
                        {mobileLabel?.toUpperCase() || label.toUpperCase()}
                    </span>
                </div>
            ))}
            {shouldShowMoreActions && (
                <div className="flex flex-col items-center justify-center flex-shrink-0 sm:hidden">
                    <ActionButton
                        icon={<MoreHorizIcon className="!w-[20px] !h-[20px]" />}
                        onClick={readOnly ? null : handleMoreClick}
                        disabled={readOnly}
                        htmlType="button"
                        type="contained"
                        autoWidth={true}
                        shape="rounded-lg"
                        tooltipText=""
                        ariaExpanded={menuOpen}
                        ref={moreButtonRef}
                        className="flex flex-col items-center justify-center !bg-secondary !text-text rounded-lg p-3 md:w-20 sm:w-18 hover:bg-gray-300 transition"
                        textClass="mt-1 text-[10px] md:text-xs text-center hidden lg:block"
                    />
                    <span className="mt-1 text-[10px] md:text-xs text-center text-text">MORE</span>

                    {menuOpen && (
                        <MoreActionDropdown
                            actions={actionIcons.slice(4, 7)}
                            onClick={handleActionClick}
                            onClose={handleCloseMenu}
                            containerClasses=" !w-[160px] absolute z-2 !opacity-100 -bottom-[98px] right-[16px] rounded-lg !bg-[#FFF] dark:!bg-[#3F3F46] border-t border-divider-text shadow-[0px_5px_14px_rgba(0,0,0,0.15)] dark:shadow-[0px_5px_14px_rgba(0,0,0,0.15)]"
                            buttonClasses="flex items-center gap-2 w-full !text-balanceCard-manage item-center rounded-lg text-sm font-medium leading-none tracking-[0.14px] uppercase dark:hover:!bg-[#51525C] hover:bg-[#F4F4F5]"
                        />
                    )}
                </div>
            )}
            {ModalComponent && (
                <ModalComponent open={true} isActionBar={true} {...commonModalProps} />
            )}
        </div>
    );
};

export default React.memo(ActionBar);
